// src/beta/components/SectionErrorBoundary.tsx
import { Component, ReactNode } from 'react';
import { Eyebrow } from '@/beta/components/Eyebrow';
import { RedRule } from '@/beta/components/RedRule';

interface Props {
  sectionName: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Per-section error boundary for the Dossier — spec § 9 (3).
 *  When a child throws during render, this catches the error and renders a
 *  compact fallback (eyebrow + retry button) inline so the rest of the
 *  dossier continues to render. Retry clears state so the child remounts. */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error(`[Dossier section: ${this.props.sectionName}]`, error);
  }

  private retry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <section style={{ padding: 'var(--vm-s-3) 0' }}>
        <Eyebrow>{this.props.sectionName.toUpperCase()} — FAILED</Eyebrow>
        <RedRule width="full" />
        <p
          style={{
            color: 'var(--vm-stone)',
            fontSize: 13,
            marginTop: 'var(--vm-s-2)',
            fontFamily: 'var(--vm-font-body)',
          }}
        >
          This section couldn't load — the rest of the dossier is fine.
        </p>
        <button
          type="button"
          onClick={this.retry}
          style={{
            marginTop: 'var(--vm-s-2)',
            background: 'transparent',
            color: 'var(--vm-red)',
            border: '1px solid var(--vm-red)',
            padding: '8px 14px',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            fontFamily: 'var(--vm-font-heading)',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </section>
    );
  }
}
